import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ReportGroupBy, ReportQueryParams, ReportTab } from '@/types/report';

export interface ReportPageState {
  tab: ReportTab;
  dateFrom: string;
  dateTo: string;
  groupBy: ReportGroupBy;
  warehouseId: string;
}

const DEFAULT_TAB: ReportTab = 'overview';
const DEFAULT_GROUP_BY: ReportGroupBy = 'day';

function defaultDateRange() {
  return {
    dateFrom: dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
    dateTo: dayjs().format('YYYY-MM-DD'),
  };
}

function parseTab(value: string | null): ReportTab {
  if (value === 'sales' || value === 'inventory' || value === 'customers') return value;
  return DEFAULT_TAB;
}

function parseGroupBy(value: string | null): ReportGroupBy {
  if (value === 'week' || value === 'month') return value;
  return DEFAULT_GROUP_BY;
}

export function useReportQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSyncedDefaults = useRef(false);

  const state = useMemo<ReportPageState>(() => {
    const defaults = defaultDateRange();
    return {
      tab: parseTab(searchParams.get('tab')),
      dateFrom: searchParams.get('dateFrom') ?? defaults.dateFrom,
      dateTo: searchParams.get('dateTo') ?? defaults.dateTo,
      groupBy: parseGroupBy(searchParams.get('groupBy')),
      warehouseId: searchParams.get('warehouseId') ?? '',
    };
  }, [searchParams]);

  useEffect(() => {
    if (hasSyncedDefaults.current) return;

    const defaults = defaultDateRange();
    const needsDateFrom = !searchParams.get('dateFrom');
    const needsDateTo = !searchParams.get('dateTo');
    const needsGroupBy = !searchParams.get('groupBy');

    if (!needsDateFrom && !needsDateTo && !needsGroupBy) {
      hasSyncedDefaults.current = true;
      return;
    }

    hasSyncedDefaults.current = true;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (needsDateFrom) next.set('dateFrom', defaults.dateFrom);
        if (needsDateTo) next.set('dateTo', defaults.dateTo);
        if (needsGroupBy) next.set('groupBy', DEFAULT_GROUP_BY);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const apiParams = useMemo<ReportQueryParams>(
    () => ({
      dateFrom: state.dateFrom,
      dateTo: state.dateTo,
      groupBy: state.groupBy,
      ...(state.warehouseId ? { warehouseId: state.warehouseId } : {}),
      limit: 25,
    }),
    [state.dateFrom, state.dateTo, state.groupBy, state.warehouseId],
  );

  const dateRangeValue = useMemo<[Dayjs, Dayjs]>(
    () => [dayjs(state.dateFrom), dayjs(state.dateTo)],
    [state.dateFrom, state.dateTo],
  );

  const patchParams = useCallback(
    (patch: Partial<ReportPageState>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (patch.tab !== undefined) next.set('tab', patch.tab);
          if (patch.dateFrom !== undefined) next.set('dateFrom', patch.dateFrom);
          if (patch.dateTo !== undefined) next.set('dateTo', patch.dateTo);
          if (patch.groupBy !== undefined) next.set('groupBy', patch.groupBy);
          if (patch.warehouseId !== undefined) {
            if (patch.warehouseId) next.set('warehouseId', patch.warehouseId);
            else next.delete('warehouseId');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetFilters = useCallback(() => {
    const range = defaultDateRange();
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('tab', state.tab);
        next.set('dateFrom', range.dateFrom);
        next.set('dateTo', range.dateTo);
        next.set('groupBy', DEFAULT_GROUP_BY);
        next.delete('warehouseId');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams, state.tab]);

  return {
    state,
    apiParams,
    dateRangeValue,
    patchParams,
    resetFilters,
  };
}
